import {
    type AttachmentListItem,
    DatasetListItem,
    LumiDB,
    RasterMetadata,
    TableMetadata,
    type HorizontalUnit,
} from "@lumidb/lumidb";
import type * as GeoJSON from "geojson";
import proj4 from "proj4";
import * as THREE from "three";
import { proxy, ref } from "valtio";
import { Auth } from "./auth";
import { ColorMode } from "./point-material";
import type { ProjectInfo } from "./project";
import {
    clearArray,
    resolveDisplayUnits,
    setRecord,
    WGS84_EPSG,
    WM_EPSG,
    type DisplayUnits,
    type UnitSystem,
} from "./utils";
export type { ProjectInfo, RoleName } from "./project";

export type DatasetKind = "pointcloud" | "raster" | "vector" | "attachment";
export type DatasetListMeta = Omit<DatasetListItem, "kind"> & { kind: DatasetKind };
export type DatasetMeta = TableMetadata & { name: string };

export function rasterBoundaryGeoJSON(
    bounds: readonly [number, number, number, number, ...number[]],
): GeoJSON.MultiPolygon {
    const [west, south, east, north] = bounds;
    return {
        type: "MultiPolygon",
        coordinates: [
            [
                [
                    [west, south],
                    [east, south],
                    [east, north],
                    [west, north],
                    [west, south],
                ],
            ],
        ],
    };
}

export function rasterTileUrlTemplate(meta: RasterMetadata): string {
    return `${window.location.origin}/api/rasters/${encodeURIComponent(meta.raster_name)}/tiles/{z}/{x}/{y}`;
}

export type FeatureCollection = {
    name: string;
    properties: GeoJSON.GeoJsonProperties | null;
    source_asset_ids?: string[];
    bounds_wgs84?: [number, number, number, number];
    feature_count?: number;
    hidden?: boolean;
};

export class App {
    public state = proxy({
        projects: {} as Record<string, ProjectInfo>,
        datasets: {} as Record<string, DatasetListMeta>,
        proj: WM_EPSG,
        sceneUnit: { meters_per_unit: 1, name: "metre" } as HorizontalUnit | null,
        selectionId: null as string | null,
        offset: { x: 0, y: 0, z: 0 },
        compare: [] as string[],
        visible: [] as string[],
        zRange: [0, 1] as [number, number],
        elevationRange: [0, 1] as [number, number],
        hagFilterRange: null as [number, number] | null,
        split: -1,
        colorMode: "rgb" as ColorMode,
        classificationMask: null as number[] | null,
        objectIdMask: [] as number[],
        pointclouds: {} as Record<string, DatasetMeta>,
        rasters: {} as Record<string, RasterMetadata>,
        attachments: {} as Record<string, AttachmentListItem>,
        collections: {} as Record<string, FeatureCollection>,
    });

    private readonly lumidb: LumiDB;
    private readonly auth: Auth;

    constructor(lumidb: LumiDB, auth: Auth) {
        this.auth = auth;
        this.lumidb = lumidb;
    }

    private wmConverters = new Map<string, proj4.Converter>();
    private wgs84Converters = new Map<string, proj4.Converter>();

    private getWmConverter(proj: string) {
        let converter = this.wmConverters.get(proj);
        if (!converter) {
            converter = proj4(proj, WM_EPSG);
            this.wmConverters.set(proj, converter);
        }
        return converter;
    }

    private getWgs84Converter(proj: string) {
        let converter = this.wgs84Converters.get(proj);
        if (!converter) {
            converter = proj4(proj, WGS84_EPSG);
            this.wgs84Converters.set(proj, converter);
        }
        return converter;
    }

    /** Always set the scene CRS through this, or the unit stays on the old one. */
    public setSceneProj(proj: string) {
        this.state.proj = proj;

        // No dataset in this CRS means a viewer-chosen one (Web Mercator), which is metric.
        const source = Object.values(this.state.pointclouds).find((d) => d.proj === proj);
        const unit = source ? source.horizontal_unit : { meters_per_unit: 1, name: "metre" };

        const scale = (this.state.sceneUnit?.meters_per_unit ?? 1) / (unit?.meters_per_unit ?? 1);
        this.state.sceneUnit = unit;
        if (scale !== 1) this.rescaleSceneLengths(scale);
    }

    private rescaleSceneLengths(scale: number) {
        this.state.zRange[0] *= scale;
        this.state.zRange[1] *= scale;
        this.state.elevationRange[0] *= scale;
        this.state.elevationRange[1] *= scale;

        if (this.state.hagFilterRange) {
            this.state.hagFilterRange = [this.state.hagFilterRange[0] * scale, this.state.hagFilterRange[1] * scale];
        }
    }

    public displayUnits(system: UnitSystem): DisplayUnits {
        return resolveDisplayUnits(system, this.state.sceneUnit);
    }

    public toSceneLength(dataset: { horizontal_unit: HorizontalUnit | null }): number {
        return (dataset.horizontal_unit?.meters_per_unit ?? 1) / (this.state.sceneUnit?.meters_per_unit ?? 1);
    }

    /** Project WGS84 bounds into the current scene CRS and attach the supplied scene-space Z range. */
    public boundsExtent(bounds: readonly [number, number, number, number, ...number[]], minZ = 0, maxZ = 0) {
        const [west, south, east, north] = bounds;
        const wgs84ToProj = proj4(WGS84_EPSG, this.state.proj);
        const corners = [
            [west, south],
            [east, south],
            [west, north],
            [east, north],
        ].map((corner) => wgs84ToProj.forward(corner));

        const xs = corners.map((corner) => corner[0]);
        const ys = corners.map((corner) => corner[1]);
        return new THREE.Box3(
            new THREE.Vector3(Math.min(...xs), Math.min(...ys), minZ),
            new THREE.Vector3(Math.max(...xs), Math.max(...ys), maxZ),
        );
    }

    public setDisplayName(datasetId: string, displayName: string) {
        const dataset = this.state.datasets[datasetId];
        if (dataset) dataset.display_name = displayName;
    }

    public fromWGS84(x: number, y: number) {
        const point = this.getWgs84Converter(this.state.proj).inverse([x, y]);

        return [point[0], point[1]];
    }

    public toWGS84(x: number, y: number) {
        const point = this.getWgs84Converter(this.state.proj).forward([x, y]);

        return point;
    }

    public isWm() {
        return this.state.proj === WM_EPSG;
    }

    public fromWm(x: number, y: number) {
        const point = this.getWmConverter(this.state.proj).inverse([x, y]);

        return point;
    }

    public toWm(x: number, y: number) {
        const point = this.getWmConverter(this.state.proj).forward([x, y]);

        return point;
    }

    public async deleteRaster(rasterId: string) {
        const name = this.state.datasets[rasterId]?.name;
        if (!name) throw new Error(`Raster ${rasterId} not found`);

        const response = await this.auth.fetch(`/api/rasters/${encodeURIComponent(name)}`, {
            method: "DELETE",
        });

        if (response.ok) {
            delete this.state.rasters[rasterId];
            delete this.state.datasets[rasterId];
        } else {
            throw new Error(await response.text());
        }
    }

    public async deleteAttachment(attachmentId: string) {
        await this.lumidb.deleteAttachment(attachmentId);
        delete this.state.attachments[attachmentId];
        delete this.state.datasets[attachmentId];
    }

    public async deleteDataset(datasetId: string) {
        const dataset = this.state.datasets[datasetId];
        if (!dataset) throw new Error(`Dataset ${datasetId} not found`);

        const response = await this.auth.fetch(`/api/tables/${dataset.name}`, {
            method: "DELETE",
            headers: {
                "content-type": "application/json",
            },
        });

        if (response.ok) {
            delete this.state.pointclouds[datasetId];
            delete this.state.datasets[datasetId];
        } else {
            throw new Error(await response.text());
        }
    }

    /** Queues an import of the original files again, thus the current import code builds a new version. */
    public async reimport(kind: "dataset" | "raster", name: string) {
        const collection = kind === "raster" ? "rasters" : "tables";
        const response = await this.auth.fetch(`/api/${collection}/${encodeURIComponent(name)}/reimport`, {
            method: "POST",
        });

        if (!response.ok) {
            throw new Error(await response.text());
        }
    }

    public adjustRanges(extent: THREE.Box3) {
        const minZ = Math.floor(extent.min.z);
        const maxZ = Math.ceil(extent.max.z);

        this.state.zRange[0] = minZ;
        this.state.zRange[1] = maxZ;
        this.state.elevationRange[0] = minZ;
        this.state.elevationRange[1] = maxZ;
    }

    public reset() {
        this.setSceneProj(WM_EPSG);
        this.state.selectionId = null;
        Object.assign(this.state.offset, { x: 0, y: 0, z: 0 });
        clearArray(this.state.compare);
        clearArray(this.state.visible);
        this.state.zRange[0] = 0;
        this.state.zRange[1] = 1;
        this.state.elevationRange[0] = 0;
        this.state.elevationRange[1] = 1;
        this.state.hagFilterRange = null;
        this.state.split = -1;
        this.state.colorMode = "rgb";
        this.state.classificationMask = null;
        clearArray(this.state.objectIdMask);
    }

    private defineProj(proj: string, proj4String: string | null) {
        if (proj4String && proj.startsWith("EPSG:") && !proj4.defs(proj)) {
            proj4.defs(proj, proj4String);
        }
    }

    /** List every dataset visible to the current user, keyed by dataset id. */
    public async listDatasets(signal?: AbortSignal): Promise<Record<string, DatasetListMeta>> {
        const response = await this.auth.fetch("/api/datasets", { signal });
        if (!response.ok) throw new Error(await response.text());
        return response.json() as Promise<Record<string, DatasetListMeta>>;
    }

    /** Load the lightweight, unified dataset index. */
    public async loadDatasets(signal?: AbortSignal) {
        const datasets = await this.listDatasets(signal);

        for (const dataset of Object.values(datasets)) {
            if (dataset.proj) this.defineProj(dataset.proj, dataset.proj4_string);
        }

        setRecord(this.state.datasets, datasets);
    }

    public projectId(datasetId: string): string | null {
        return this.state.datasets[datasetId]?.project_id ?? null;
    }

    private datasetMetadataRequests = new Map<string, Promise<void>>();

    private hasDatasetMeta(datasetId: string, kind: DatasetKind): boolean {
        if (kind === "pointcloud") return this.state.pointclouds[datasetId] !== undefined;
        if (kind === "raster") return this.state.rasters[datasetId] !== undefined;
        if (kind === "attachment") return this.state.attachments[datasetId] !== undefined;
        return this.state.collections[datasetId] !== undefined;
    }

    public loadDatasetMeta(datasetId: string, refresh = false): Promise<void> {
        const item = this.state.datasets[datasetId];
        if (!item) throw new Error("Dataset not found");

        if (!refresh && this.hasDatasetMeta(datasetId, item.kind)) return Promise.resolve();

        const pending = this.datasetMetadataRequests.get(datasetId);
        if (!refresh && pending) return pending;

        const request = this.auth
            .fetch(`/api/${item.kind}s/${encodeURIComponent(datasetId)}`)
            .then(async (response) => {
                if (!response.ok) throw new Error(await response.text());
                return response.json() as Promise<unknown>;
            })
            .then((metadata) => {
                if (item.kind === "pointcloud") {
                    const meta = metadata as TableMetadata;
                    this.state.pointclouds[datasetId] = {
                        ...meta,
                        name: item.name,
                        boundary: ref(meta.boundary),
                    };
                    this.defineProj(meta.proj, meta.proj4_string);
                } else if (item.kind === "raster") {
                    const raster = metadata as RasterMetadata;
                    this.state.rasters[datasetId] = raster;
                    this.defineProj(raster.table_proj, raster.proj4_string);
                } else if (item.kind === "attachment") {
                    this.state.attachments[datasetId] = metadata as AttachmentListItem;
                } else {
                    const hidden = this.state.collections[datasetId]?.hidden;
                    this.state.collections[datasetId] = { ...(metadata as FeatureCollection), hidden };
                }
            })
            .finally(() => {
                this.datasetMetadataRequests.delete(datasetId);
            });

        this.datasetMetadataRequests.set(datasetId, request);
        return request;
    }
}
